import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Outlet } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { onAuthStateChanged, User } from 'firebase/auth'
import debounce from 'lodash.debounce'
import { linkDeviceToUser } from 'api'
import PromoModal from 'components/modals/PromoModal'
import LogInModal from 'components/modals/LogInModal'
import SignUpModal from 'components/modals/SignUpModal'
import Toastify from 'components/helpers/Toastify'
import MainLayoutContext, {
  MainLayoutContextInterface,
  Stage,
} from 'contexts/MainLayoutContext'
import useModal from 'hooks/useModal'
import { auth } from 'utils/firebase'
import getDeviceId from 'utils/getDeviceId'
import getLanguage from 'utils/getLanguage'
import MainLayoutHeader from './MainLayoutHeader'
import c from './MainLayout.module.scss'

function MainLayout() {
  const { i18n } = useTranslation()

  const {
    isOpen: isPromoModalOpen,
    open: openPromoModal,
    close: closePromoModal,
  } = useModal()

  const {
    isOpen: isLogInModalOpen,
    open: openLogInModal,
    close: closeLogInModal,
  } = useModal()

  const {
    isOpen: isSignUpModalOpen,
    open: openSignUpModal,
    close: closeSignUpModal,
  } = useModal()

  const [user, setUser] = useState<User | null>(null)
  const [language, setLanguage] = useState<string>(getLanguage())
  const [deviceId, setDeviceId] = useState<string>(getDeviceId())
  const [stage, setStage] = useState<Stage>('idle')

  const [processedImageUrl, setProcessedImageUrl] = useState<string | null>(
    null,
  )

  const mainLayoutContextValue = useMemo<MainLayoutContextInterface>(() => {
    return {
      user,
      language,
      setLanguage,
      deviceId,
      setDeviceId,
      stage,
      setStage,
      processedImageUrl,
      setProcessedImageUrl,
      openLogInModal,
      openSignUpModal,
    }
  }, [
    user,
    language,
    deviceId,
    stage,
    processedImageUrl,
    openLogInModal,
    openSignUpModal,
  ])

  const setHeight = useCallback(
    debounce(() => {
      const height = window.innerHeight
      document.documentElement.style.setProperty('--height', `${height}px`)
    }, 100),
    [],
  )

  useEffect(() => {
    setHeight()
    window.addEventListener('resize', setHeight)

    return () => {
      window.removeEventListener('resize', setHeight)
    }
  }, [])

  useEffect(() => {
    return onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser)
      if (currentUser)
        currentUser
          .getIdToken()
          .then((token) => linkDeviceToUser(deviceId, token))
    })
  }, [])

  useEffect(() => {
    const isShown = window.localStorage.getItem('neuro-art-promo-modal-shown')
    if (isShown) return
    window.localStorage.setItem('neuro-art-promo-modal-shown', 'true')
    openPromoModal()
  }, [openPromoModal])

  useEffect(() => {
    window.localStorage.setItem('lng', language)
    i18n.changeLanguage(language)
  }, [language])

  useEffect(() => {
    window.localStorage.setItem('device-id', deviceId)
  }, [deviceId])

  return (
    <MainLayoutContext.Provider value={mainLayoutContextValue}>
      <div className={c.layout}>
        <MainLayoutHeader />
        <Outlet />
        <PromoModal open={isPromoModalOpen} onClose={closePromoModal} />
        <LogInModal open={isLogInModalOpen} onClose={closeLogInModal} />
        <SignUpModal open={isSignUpModalOpen} onClose={closeSignUpModal} />
        <Toastify />
      </div>
    </MainLayoutContext.Provider>
  )
}

export default MainLayout
